import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Loader2, CalendarCheck, Trophy, AlertTriangle, Lightbulb } from "lucide-react";

export default function Reviews() {
  const [reviews, setReviews] = useState(null);
  const [gen, setGen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/weekly-reviews");
    setReviews(data.reviews);
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGen(true);
    try { await api.post("/weekly-reviews/generate"); await load(); toast.success("Weekly review ready."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setGen(false); }
  };

  if (!reviews) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF7E67]" /></div>;

  return (
    <div className="space-y-8" data-testid="reviews-page">
      <div className="flex items-center justify-between">
        <h1 className="font-nunito font-extrabold tracking-tight text-4xl text-[#2D3748] sm:text-5xl">Weekly Reviews</h1>
        <button onClick={generate} disabled={gen} data-testid="generate-review-btn"
          className="flex items-center gap-2 rounded-full bg-sunrise px-5 py-3 text-sm font-bold tracking-wide text-[#2D3748] transition-all hover:scale-105 disabled:opacity-70">
          {gen ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CalendarCheck className="h-4 w-4" /> Run Review</>}
        </button>
      </div>

      {reviews.length === 0 && (
        <div className="rounded-3xl border border-[#FFE8E3] bg-white p-12 text-center text-[#718096]">
          <CalendarCheck className="mx-auto h-10 w-10 text-[#FF7E67]" />
          <p className="mt-4">No reviews yet. Run your first weekly review to see wins and next-week priorities.</p>
        </div>
      )}

      {reviews.map((r) => {
        const rv = r.review || {};
        return (
          <div key={r.id} className="rounded-3xl border border-[#FFE8E3] bg-white p-7">
            <p className="text-xs text-[#718096]">{new Date(r.created_at).toLocaleDateString()}</p>
            {rv.completion_forecast && <p className="mt-2 rounded-xl border border-[#FFE8E3] bg-[#FFF4E6] px-4 py-3 text-sm text-[#2D3748]">{rv.completion_forecast}</p>}
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Block icon={Trophy} title="Wins" items={rv.wins} />
              <Block icon={AlertTriangle} title="Needs Attention" items={rv.attention_areas} />
              <Block icon={Lightbulb} title="Lessons" items={rv.lessons} />
              <Block icon={CalendarCheck} title="Next Week" items={rv.next_week_priorities} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const Block = ({ icon: Icon, title, items }) => (
  <div>
    <p className="mb-2 flex items-center gap-2 font-nunito text-sm font-bold tracking-wide text-[#FF7E67]"><Icon className="h-4 w-4" /> {title}</p>
    <ul className="space-y-1.5">
      {(items || []).map((x, i) => <li key={i} className="flex gap-2 text-sm text-[#4A5568]"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-sunrise" />{x}</li>)}
      {(!items || items.length === 0) && <li className="text-sm text-[#718096]">—</li>}
    </ul>
  </div>
);
